import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Copy, Plus, X } from "lucide-react";

export interface DaySchedule {
  day_of_week: number;
  is_enabled: boolean;
  start_time: string;
  end_time: string;
  /** Início do intervalo (ex.: almoço) — HH:MM. Null/undefined = sem intervalo. */
  break_start?: string | null;
  /** Fim do intervalo — HH:MM. Null/undefined = sem intervalo. */
  break_end?: string | null;
}

interface BusinessHoursSelectorProps {
  value: DaySchedule[];
  onChange: (schedules: DaySchedule[]) => void;
}

const dayNames = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const dayInitials = ["D", "S", "T", "Q", "Q", "S", "S"];

/** "8:00", "08:00", "08:00:00" → minutos. Vazio/inválido → null. */
function toMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/**
 * Mesmas regras do banco (agent_business_hours_break_range_chk).
 * Retorna a primeira mensagem de erro, com o nome do dia, ou null se estiver tudo certo.
 */
export function validateBusinessHours(schedules: DaySchedule[]): string | null {
  for (const s of [...schedules].sort((a, b) => a.day_of_week - b.day_of_week)) {
    if (!s.is_enabled) continue;
    const day = dayNames[s.day_of_week] ?? `Dia ${s.day_of_week}`;
    const start = toMinutes(s.start_time);
    const end = toMinutes(s.end_time);
    if (start === null || end === null) return `${day}: preencha o horário de abertura e de fechamento.`;
    if (start >= end) return `${day}: o horário de fechamento precisa ser depois da abertura.`;
    const hasBreakStart = !!s.break_start;
    const hasBreakEnd = !!s.break_end;
    if (!hasBreakStart && !hasBreakEnd) continue;
    const bs = toMinutes(s.break_start);
    const be = toMinutes(s.break_end);
    if (bs === null || be === null) return `${day}: preencha o início e o fim do intervalo, ou remova o intervalo.`;
    if (bs >= be) return `${day}: o fim do intervalo precisa ser depois do início.`;
    if (bs < start || be > end) {
      return `${day}: o intervalo (${s.break_start?.slice(0, 5)}–${s.break_end?.slice(0, 5)}) precisa ficar dentro do horário de funcionamento (${s.start_time.slice(0, 5)}–${s.end_time.slice(0, 5)}).`;
    }
  }
  return null;
}

export function BusinessHoursSelector({ value, onChange }: BusinessHoursSelectorProps) {
  const [enabled, setEnabled] = useState(() => {
    return value.length > 0 && value.some(h => h.is_enabled);
  });

  useEffect(() => {
    const hasEnabled = value.length > 0 && value.some(h => h.is_enabled);
    setEnabled(hasEnabled);
  }, [value]);

  // Initialize with 7 days, filling in missing days
  const schedules = Array.from({ length: 7 }, (_, i) => {
    const existing = value.find(s => s.day_of_week === i);
    return existing || {
      day_of_week: i,
      is_enabled: false,
      start_time: "08:00",
      end_time: "18:00",
    };
  });

  const handleToggleEnabled = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onChange([]);
    } else {
      const hasEnabledHours = value.length > 0 && value.some(h => h.is_enabled);
      const toRestore = hasEnabledHours 
        ? value 
        : Array.from({ length: 7 }, (_, i) => ({
            day_of_week: i,
            is_enabled: true,
            start_time: "08:00",
            end_time: "18:00",
          }));
      onChange(toRestore);
    }
  };

  const handleDayToggle = (dayIndex: number) => {
    // Garantir que sempre trabalhamos com o array completo de 7 dias
    const currentSchedules = Array.from({ length: 7 }, (_, i) => {
      const existing = schedules.find(s => s.day_of_week === i);
      return existing || {
        day_of_week: i,
        is_enabled: true,
        start_time: "08:00",
        end_time: "18:00",
      };
    });
    
    const updated = currentSchedules.map((s) =>
      s.day_of_week === dayIndex ? { ...s, is_enabled: !s.is_enabled } : s
    );
    onChange(updated);
  };

  const handleTimeChange = (dayIndex: number, field: "start_time" | "end_time" | "break_start" | "break_end", value: string) => {
    const updated = schedules.map((s) =>
      s.day_of_week === dayIndex ? { ...s, [field]: value } : s
    );
    onChange(updated);
  };

  const handleToggleBreak = (dayIndex: number) => {
    const updated = schedules.map((s) => {
      if (s.day_of_week !== dayIndex) return s;
      const hasBreak = !!(s.break_start && s.break_end);
      if (hasBreak) {
        return { ...s, break_start: null, break_end: null };
      }
      // Sugestão inicial: 12:00 - 13:00, respeitando janela do dia
      const suggestStart = s.start_time <= "12:00" && "12:00" < s.end_time ? "12:00" : s.start_time;
      const suggestEnd = suggestStart < "13:00" && "13:00" <= s.end_time ? "13:00" : s.end_time;
      return { ...s, break_start: suggestStart, break_end: suggestEnd };
    });
    onChange(updated);
  };

  const handleCopyToAll = (sourceDay: number) => {
    const source = schedules.find((s) => s.day_of_week === sourceDay);
    if (!source) return;

    const updated = schedules.map((s) => ({
      ...s,
      start_time: source.start_time,
      end_time: source.end_time,
      break_start: source.break_start ?? null,
      break_end: source.break_end ?? null,
    }));
    onChange(updated);
  };

  if (!enabled) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Horário de Funcionamento</Label>
          <Switch checked={enabled} onCheckedChange={handleToggleEnabled} />
        </div>
        <p className="text-sm text-muted-foreground">
          Ative para configurar horários específicos por dia da semana
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Horário de Funcionamento</Label>
        <Switch checked={enabled} onCheckedChange={handleToggleEnabled} />
      </div>

      <div className="space-y-3">
        {schedules.map((schedule) => (
          <div key={schedule.day_of_week} className="space-y-2">
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant={schedule.is_enabled ? "default" : "outline"}
                size="sm"
                className="w-10 h-10 p-0"
                onClick={() => handleDayToggle(schedule.day_of_week)}
              >
                {dayInitials[schedule.day_of_week]}
              </Button>
              
              <span className="w-24 text-sm font-medium">
                {dayNames[schedule.day_of_week]}
              </span>

              {schedule.is_enabled ? (
                <>
                  <input
                    type="time"
                    value={schedule.start_time}
                    onChange={(e) => handleTimeChange(schedule.day_of_week, "start_time", e.target.value)}
                    className="px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <span className="text-sm text-muted-foreground">até</span>
                  <input
                    type="time"
                    value={schedule.end_time}
                    onChange={(e) => handleTimeChange(schedule.day_of_week, "end_time", e.target.value)}
                    className="px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleBreak(schedule.day_of_week)}
                    title={schedule.break_start && schedule.break_end ? "Remover intervalo" : "Adicionar intervalo (ex.: almoço)"}
                  >
                    {schedule.break_start && schedule.break_end ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyToAll(schedule.day_of_week)}
                    title="Copiar para todos os dias"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">Fechado</span>
              )}
            </div>
            {schedule.is_enabled && schedule.break_start && schedule.break_end && (
              <div className="flex items-center gap-3 pl-[52px]">
                <span className="w-24 text-xs text-muted-foreground">Intervalo</span>
                <input
                  type="time"
                  value={schedule.break_start}
                  onChange={(e) => handleTimeChange(schedule.day_of_week, "break_start", e.target.value)}
                  className="px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
                <span className="text-sm text-muted-foreground">até</span>
                <input
                  type="time"
                  value={schedule.break_end}
                  onChange={(e) => handleTimeChange(schedule.day_of_week, "break_end", e.target.value)}
                  className="px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="text-xs text-muted-foreground">
        💡 Clique nos botões com iniciais para ativar/desativar dias. Use <Plus className="inline h-3 w-3" /> para adicionar um intervalo (almoço) e <Copy className="inline h-3 w-3" /> para copiar o horário a todos os dias.
      </div>
    </div>
  );
}

